import { cn } from "@/lib/utils";
import { AlertTriangle, Home, RotateCcw } from "lucide-react";
import { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  resetKey?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /**
   * Quantas vezes o usuário já tentou remontar. Um erro determinístico
   * re-lança no mesmo instante, e o botão "Tentar de novo" parece morto —
   * some depois da primeira tentativa para mandar o usuário direto ao
   * "Recarregar", que resolve o caso de bundle velho.
   */
  retries: number;
}

/**
 * Rede de segurança de último recurso. Antes esta tela mostrava
 * "An unexpected error occurred." + o stack cru — em inglês, com paths de
 * bundle, para um usuário que só queria encerrar um atendimento. Agora:
 *
 *  - texto em PT-BR dizendo o que fazer;
 *  - stack só em DEV (em produção não ajuda o usuário e expõe interno);
 *  - "Tentar de novo" remonta a árvore sem recarregar, porque a maioria dos
 *    erros de render é transiente (um dado inesperado numa lista);
 *  - "Ir para o início" existe porque, com a app inteira substituída, não
 *    sobra menu para o usuário sair da tela.
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, retries: 0 };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Fica no console do navegador: é o único rastro quando o suporte pede
    // print da tela e o usuário manda também o console.
    console.error("[ErrorBoundary]", error, info.componentStack);
  }

  componentDidUpdate(prevProps: Props) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      // Navegou para outra tela: é um contexto novo, o contador de tentativas
      // da tela anterior não vale mais.
      this.setState({ hasError: false, error: null, retries: 0 });
    }
  }

  render() {
    if (this.state.hasError) {
      const buttonBase =
        "flex items-center gap-2 px-4 py-2 rounded-lg text-sm cursor-pointer hover:opacity-90";

      return (
        <div className="flex items-center justify-center min-h-screen p-8 bg-background">
          <div className="flex flex-col items-center w-full max-w-lg p-8 text-center">
            <AlertTriangle size={48} className="text-destructive mb-6 flex-shrink-0" />

            <h2 className="text-xl font-semibold mb-2">Algo deu errado nesta tela</h2>
            <p className="text-sm text-muted-foreground mb-6">
              {this.state.retries === 0
                ? "O erro foi registrado. Tente de novo — se continuar acontecendo, avise o suporte contando o que você estava fazendo."
                : "O erro se repetiu. Recarregue a página; se continuar, avise o suporte contando o que você estava fazendo."}
            </p>

            {import.meta.env.DEV && this.state.error && (
              <div className="p-4 w-full rounded bg-muted overflow-auto mb-6 text-left">
                <pre className="text-xs text-muted-foreground whitespace-break-spaces">
                  {this.state.error.stack ?? this.state.error.message}
                </pre>
              </div>
            )}

            <div className="flex flex-wrap justify-center gap-2">
              {this.state.retries === 0 && (
                <button
                  onClick={() =>
                    this.setState((s) => ({ hasError: false, error: null, retries: s.retries + 1 }))
                  }
                  className={cn(buttonBase, "bg-primary text-primary-foreground")}
                >
                  <RotateCcw size={16} />
                  Tentar de novo
                </button>
              )}
              <button
                onClick={() => window.location.reload()}
                className={cn(
                  buttonBase,
                  // Vira a ação principal quando "Tentar de novo" já falhou.
                  this.state.retries === 0
                    ? "border bg-background"
                    : "bg-primary text-primary-foreground",
                )}
              >
                <RotateCcw size={16} />
                Recarregar página
              </button>
              <button
                onClick={() => { window.location.href = "/"; }}
                className={cn(buttonBase, "border bg-background")}
              >
                <Home size={16} />
                Ir para o início
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
